import { el, clear } from "./dom.js";
import { finishCounts } from "../utils/year-review.js";
import { MAG, magGlass, magButton, magCover } from "./magazine/kit.js";
import { showActionSheet } from "./item-menu.js";
import { showToast } from "./toast.js";
import { navigate } from "../main.js";
import { listItemHistory, removeHistoryEvent, setHistoryEventDate } from "../db/history.js";
import { eventLabel } from "../utils/history.js";
import { formatReleaseDate } from "../utils/dates.js";

// History building blocks shared by the History screen and the detail screens
// (design_documents/history.md): one event row (with its ⋯ corrections) and the
// per-item "History" section that sits at the bottom of every detail view.

const DATE_STYLE = {
	fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8.5px",
	letterSpacing: ".08em", textTransform: "uppercase", color: MAG.dim,
};

function dayOf(iso) {
	return formatReleaseDate(String(iso ?? "").slice(0, 10));
}

// "Change date" / "Remove from history" for one event. Resolves after the change.
export async function editHistoryEvent(event, onChanged) {
	await showActionSheet(eventLabel(event), [
		{
			label: "Change date",
			run: async () => {
				const date = await askDate(String(event.at).slice(0, 10));
				if (!date) return;
				try {
					await setHistoryEventDate(event.id, date);
					onChanged?.();
				} catch (err) {
					showToast(err?.message ?? "Could not change the date.");
				}
			},
		},
		{
			label: "Remove from history",
			run: async () => {
				await removeHistoryEvent(event.id);
				showToast("Removed from history.");
				onChanged?.();
			},
		},
	]);
}

function askDate(initial) {
	return new Promise((resolve) => {
		const input = el("input.field__select", { type: "date", value: initial, max: new Date().toISOString().slice(0, 10) });
		const done = (v) => {
			overlay.remove();
			resolve(v);
		};
		const overlay = el(
			"div.modal-overlay",
			{ onclick: (e) => e.target === overlay && done(null) },
			el(
				"div.modal",
				{},
				el("h2.modal__title", {}, "When did it happen?"),
				el("div.field", {}, input),
				el(
					"div",
					{ style: { display: "flex", gap: "8px", marginTop: "10px" } },
					magButton("Save", { block: true, onClick: () => done(input.value || null) }),
					magButton("Cancel", { variant: "ghost", block: true, onClick: () => done(null) }),
				),
			),
		);
		document.body.appendChild(overlay);
		input.focus();
	});
}

// One event as a row. `withItem` shows the item's cover + title (the History
// screen); without it the row is just date + what happened (a detail screen).
export function historyRow(event, { withItem = true, onChanged } = {}) {
	const dim = event.reconstructed;
	const more = el("button", {
		type: "button",
		dataset: { action: "edit" },
		"aria-label": "Change or remove",
		onclick: (e) => {
			e.stopPropagation();
			editHistoryEvent(event, onChanged);
		},
		style: { flex: "none", background: "none", border: "0", color: MAG.dim, fontSize: "18px", cursor: "pointer", padding: "0 4px" },
	}, "⋯");
	const text = el(
		"div",
		{ style: { flex: "1", minWidth: "0" } },
		withItem
			? el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "13px", color: MAG.ink,
						overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
					},
				}, event.title ?? "Untitled")
			: null,
		el("div", { style: { fontFamily: "'Archivo', sans-serif", fontSize: "12px", color: withItem ? "#cfcfd6" : MAG.ink } }, eventLabel(event)),
		el("div", { style: DATE_STYLE }, `${dayOf(event.at)}${dim ? " · from saved dates" : ""}`),
	);
	const kids = [withItem ? magCover({ cover_url: event.cover_url, media_type: event.media_type }, { height: "48px", width: "36px", radius: "6px" }) : null, text, more];
	return magGlass(kids, {
		role: withItem && event.route ? "button" : undefined,
		onClick: withItem && event.route ? () => navigate(event.route) : undefined,
		style: { display: "flex", alignItems: "center", gap: "10px", padding: "8px 10px", opacity: dim ? ".7" : "1" },
	});
}

// The per-item History section for a detail screen: the latest few events and a
// link to the full, filtered History. Fills itself in asynchronously.
export function historySection(item, { limit = 5 } = {}) {
	const list = el("div", { style: { display: "flex", flexDirection: "column", gap: "6px" } });
	const wrap = el(
		"div",
		{ style: { padding: "14px 16px 0" } },
		el("div", { style: { display: "flex", alignItems: "baseline", justifyContent: "space-between" } },
			el("div", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "9.5px",
					letterSpacing: ".14em", textTransform: "uppercase", color: MAG.lime, marginBottom: "6px",
				},
			}, "History"),
			el("button", {
				type: "button",
				onclick: () => navigate(`/history?item=${encodeURIComponent(item.id)}`),
				style: { ...DATE_STYLE, background: "none", border: "0", cursor: "pointer", color: MAG.lime },
			}, "All ›"),
		),
		list,
	);
	async function fill() {
		const events = await listItemHistory(item.id);
		clear(list);
		if (events.length === 0) {
			list.append(el("div", { style: { ...DATE_STYLE, textTransform: "none", letterSpacing: "0", fontSize: "11.5px", fontWeight: "500" } },
				"Nothing yet — changes you make from now on are recorded here."));
			return;
		}
		// True "times finished" (roadmap #4): every time History saw it set to Done.
		const finishes = finishCounts(events).get(item.id) ?? 0;
		if (finishes > 0) {
			list.append(el("div", { style: { ...DATE_STYLE, textTransform: "none", letterSpacing: "0", fontSize: "12px", fontWeight: "800", color: MAG.ink, marginBottom: "4px" } },
				`Finished ${finishes === 1 ? "once" : `${finishes} times`} (counted from History)`));
		}
		for (const e of events.slice(0, limit)) list.append(historyRow(e, { withItem: false, onChanged: fill }));
	}
	fill();
	return wrap;
}
